import { AdapterContext } from './adapter-context';
import { IAttribute, IModel } from '../base/annotation';

export class ValueAdapter {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static toResource(object: {}, attribute: IAttribute, stringLiteralTemplateValue: boolean = false): any {
        const value = attribute.templateValue(object);
        if (value !== undefined) {
            return stringLiteralTemplateValue
                ? value
                : {
                    value,
                    type: 'Expression'
                };
        } else {
            return attribute.value(object);
        }
    }

    /**
     * Performs a object - spec in spirit(handles arrays and collection more meaningfully)
     * Knows how deep to traverse
     */
    public static remainder(context: AdapterContext, object: {}, model: IModel): Promise<{}> | undefined {
        const spec = context.adapterSpecFor(model);
        return spec ? ValueAdapter.remainderWithExternalSpec(context, object, spec) : undefined;
    }

    public static async remainderWithExternalSpec(context: AdapterContext, object: {}, spec: {} = {}): Promise<{}> {
        // Follow the object model on both sides, handle arrays and delegations appropriately
        // We don't need to worry about prototypes and their property names
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const remaining = {};
        for (const property in object) {
            const value = (object as any)[property];
            const specValue = (spec as any)[property];
            if (specValue === '*' || specValue === 'secret' || specValue === 'akv') {
                continue;
            } else if (typeof specValue === 'function') {
                const output = [];
                if (Array.isArray(value)) {
                    for (const item of value) {
                        const action = await specValue(context, item);
                        if (action === '*' || action === '?' || action === 'secret' || action === 'akv') { // someone else will handle it
                            continue;
                        } else {
                            output.push(item);
                        }
                    }
                    if (output.length) {
                        (remaining as any)[property] = output;
                    }
                } else {
                    const action = await specValue(context, value);
                    if (action === '*' || action === '?' || action === 'secret' || action === 'akv') {
                        continue;
                    }

                    if (typeof value === 'object' && typeof action === 'object' && !Array.isArray(action)) {
                        // Address EC control issue, when sepc is controlled by an EC, then it's better to define the action as a function which returns an object.
                        // An example: connectionProperties: () => GlobalVariables.xxx ? {...} : undefined
                        const propertyValue = await this._getRecursiveReminder(context, value, action);
                        if (propertyValue !== undefined && JSON.stringify(propertyValue) !== '{}') {
                            (remaining as any)[property] = propertyValue;
                        }
                        continue;
                    }

                    (remaining as any)[property] = value;
                }
            } else if (Array.isArray(value)) {
                const output = [];
                for (const item of value) {
                    const propertyValue = await this._getRecursiveReminder(context, item, specValue);
                    if (typeof propertyValue === 'object' && !Array.isArray(propertyValue)) {
                        // Only add the object if it has at least one key
                        output.push(Object.keys(propertyValue || {}).length ? propertyValue : null);
                    } else {
                        // for other type of values, e.g., number, string, array, boolean, let's keep them as is
                        output.push(propertyValue);
                    }
                }
                // Only add the array if at least one item has a remainder
                if (output.length && output.some((item) => !!item)) {
                    (remaining as any)[property] = output;
                }
            } else {
                const propertyValue = await this._getRecursiveReminder(context, value, specValue);

                if (propertyValue !== undefined && JSON.stringify(propertyValue) !== '{}') {
                    (remaining as any)[property] = propertyValue;
                }
            }
        }

        return remaining;
    }

    /**
     * Determines whether the given object attribute has a template value in it or just a regular value.
     * @param object The object containing the attribute.
     * @param attribute The attribute to check.
     */
    public static isTemplated(object: {}, attribute: IAttribute): boolean {
        return attribute.templateValue(object) !== undefined;
    }

    private static _getRecursiveReminder(context: AdapterContext, value: any, specValue: any): Promise<any> {
        // Recurse and continue scanning after setting the property to a dummy
        return (typeof specValue === 'object') ?
            ValueAdapter.remainderWithExternalSpec(context, value, specValue)
            : value;
    }
}
